<template>
  <div>
    <b-card class="planner-list-card student-docs-list-card">
      <div class="gradient-line-wrapper"></div>
      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 resource-title">Student Documents</h5>
          <small class="text-muted">Manage student document records</small>
        </div>
      </div>

      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2 flex-wrap"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              :key="'page-size-' + perPage"
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
              @option:selected="closePageSizeDropdown"
              ref="pageSizeSelect"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @change="onPageChange"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center">
          <div
            class="filter-fields-group filter-fields-group--search-only d-flex align-items-center"
          >
            <b-form-input
              v-model.trim="searchQuery"
              placeholder="Search by student…"
              class="filter-search-input"
              debounce="400"
            />
          </div>

          <div v-if="headOfficeObj ? isHeadOffice : true" class="filter-actions-group d-flex align-items-center ml-auto">
            <b-button
              variant="outline-success"
              class="action-btn-filter text-nowrap"
              :disabled="exporting || dataLoading"
              @click="exportData"
            >
              <b-spinner
                v-if="exporting"
                variant="success"
                small
                class="mr-50"
              />
              <feather-icon
                v-else
                icon="DownloadIcon"
                size="16"
                class="mr-50"
              />
              {{ $t("Excel") }}
            </b-button>
            <b-button
              v-if="rights.add || rights.edit"
              variant="gradient-primary"
              @click="openDocTypesModal"
              class="action-btn-filter text-nowrap"
              :disabled="dataLoading"
            >
              <feather-icon icon="SettingsIcon" size="16" class="mr-50" />
              Manage Document Types
            </b-button>
          </div>
        </b-col>

        <b-col cols="12" class="mt-1 filter-toolbar d-flex align-items-center">
          <div class="filter-fields-group d-flex align-items-center flex-wrap">
            <v-select
              v-if="isHeadOffice"
              v-model="selectedCampusID"
              :options="campuses"
              label="branch"
              :reduce="(c) => c.id"
              :clearable="false"
              placeholder="Campus"
              class="filter-control filter-field-item"
              style="width: 220px; max-width: 100%;"
              :disabled="dataLoading"
              @input="onCampusFilterChange"
            />
            <v-select
              v-model="docFilter"
              :options="[{ id: 0, title: 'All Documents' }, ...docTypes]"
              :reduce="(o) => o.id"
              label="title"
              :clearable="false"
              :searchable="false"
              class="filter-control filter-field-item"
              style="width: 220px; max-width: 100%;"
              :disabled="docTypesLoading"
              placeholder="Document"
              @input="onDocFilterChange"
            />
            <v-select
              v-model="statusFilter"
              :options="statusFilterOptions"
              :reduce="(o) => o.value"
              label="label"
              :clearable="false"
              :searchable="false"
              class="filter-control filter-field-item"
              style="width: 220px; max-width: 100%;"
              :disabled="dataLoading"
              placeholder="Status"
              @input="onStatusFilterChange"
            />
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <div
          class="table-scrollbar-top mt-25"
          ref="topScrollbar"
          @scroll="syncScroll"
        >
          <div class="table-scrollbar-content"></div>
        </div>

        <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
          <b-table
            class="planner-list-table student-docs-table mt-1"
            :items="documents"
            :fields="fields"
            :busy="dataLoading"
            :responsive="false"
            hover
            show-empty
            empty-text="No documents found"
          >
            <template #table-busy>
              <tbody>
                <tr v-for="n in 6" :key="'skeleton-' + n" class="skeleton-row">
                  <td
                    v-for="(field, index) in fields"
                    :key="'skeleton-cell-' + index"
                    class="skeleton-cell"
                  >
                    <div class="skeleton-loader"></div>
                  </td>
                </tr>
              </tbody>
            </template>

            <template #cell(student)="{ item }">
              <b-media vertical-align="center">
                <template #aside>
                  <b-avatar
                    size="44"
                    :src="item.picture"
                    variant="light-primary"
                  />
                </template>
                <span class="font-weight-bold d-block text-nowrap">
                  {{ item.name || "—" }}
                </span>
                <small class="text-muted d-block text-nowrap">
                  F/N: {{ item.father_name || "—" }}
                </small>
                <small class="text-muted d-block text-nowrap">
                  <span
                    v-if="
                      ['myskooltcfdemo', 'myskooltcf'].includes(reportDomain)
                    "
                  >
                    FB Reg. No.
                    {{
                      item.studentDetails
                        ? item.studentDetails.federalRegNo || "—"
                        : "—"
                    }}
                  </span>
                  <span v-else> GR: {{ item.grNo || "—" }} </span>
                  <span v-if="item.meta_class" class="ml-50"
                    >| {{ item.meta_class }}</span
                  >
                </small>
              </b-media>
            </template>

            <template #cell(campus)="data">
              <b-badge variant="light-primary">
                {{ getCampus(data.item.campusID) }}
              </b-badge>
            </template>

            <template #cell(status)="{ item }">
              <div class="docs-status-wrap" v-if="item.docsStatus">
                <b-badge variant="light-secondary" class="status-pill mr-25">
                  <feather-icon icon="FileIcon" size="11" class="mr-25" />
                  {{ item.docsStatus.total }} Total
                </b-badge>
                <b-badge
                  v-if="item.docsStatus.pending"
                  variant="light-warning"
                  class="status-pill mr-25"
                >
                  <feather-icon icon="ClockIcon" size="11" class="mr-25" />
                  {{ item.docsStatus.pending }} Pending
                </b-badge>
                <b-badge
                  v-if="item.docsStatus.approved"
                  variant="light-success"
                  class="status-pill mr-25"
                >
                  <feather-icon
                    icon="CheckCircleIcon"
                    size="11"
                    class="mr-25"
                  />
                  {{ item.docsStatus.approved }} Approved
                </b-badge>
                <b-badge
                  v-if="item.docsStatus.rejected"
                  variant="light-danger"
                  class="status-pill mr-25"
                >
                  <feather-icon icon="XCircleIcon" size="11" class="mr-25" />
                  {{ item.docsStatus.rejected }} Rejected
                </b-badge>
                <b-badge
                  v-if="item.docsStatus.notUploaded"
                  variant="light-secondary"
                  class="status-pill"
                >
                  <feather-icon icon="UploadIcon" size="11" class="mr-25" />
                  {{ item.docsStatus.notUploaded }} Not Uploaded
                </b-badge>
              </div>
              <span v-else class="text-muted">—</span>
            </template>

            <template #cell(actions)="{ item }">
              <b-button
                v-if="rights.edit"
                variant="gradient-primary"
                size="sm"
                v-b-tooltip.hover.left
                title="View Documents"
                class="action-btn-table"
                :disabled="dataLoading"
                @click="openStudentDocs(item)"
              >
                <feather-icon icon="FolderIcon" size="16" />
              </b-button>
            </template>
          </b-table>
        </div>
      </div>
    </b-card>

    <!-- Student Documents Detail Modal -->
    <b-modal
      v-model="showStudentDocsModal"
      size="xl"
      centered
      scrollable
      modal-class="student-docs-modal"
      content-class="student-docs-modal-content"
      hide-header
      @hidden="onStudentDocsModalHidden"
    >
      <div class="student-docs-modal-header">
        <div class="student-docs-modal-header__info">
          <b-avatar
            v-if="selectedStudent"
            size="48"
            :src="selectedStudent.picture"
            variant="light-primary"
            class="mr-1"
          />
          <div>
            <h5 class="student-docs-modal-title mb-0">
              {{ selectedStudent ? selectedStudent.name : "Student Documents" }}
            </h5>
            <small class="text-muted d-block" v-if="selectedStudent">
              {{ selectedStudent.meta_class || "—" }}
              <span v-if="selectedStudent.grNo">
                · GR {{ selectedStudent.grNo }}</span
              >
            </small>
          </div>
        </div>
        <div
          class="student-docs-modal-header__stats"
          v-if="!studentDocsLoading"
        >
          <b-badge variant="light-warning" class="status-pill mr-25">
            <feather-icon icon="ClockIcon" size="11" class="mr-25" />
            {{ modalDocStats.pending }} Pending
          </b-badge>
          <b-badge variant="light-success" class="status-pill mr-25">
            <feather-icon icon="CheckCircleIcon" size="11" class="mr-25" />
            {{ modalDocStats.approved }} Approved
          </b-badge>
          <b-badge variant="light-danger" class="status-pill">
            <feather-icon icon="XCircleIcon" size="11" class="mr-25" />
            {{ modalDocStats.rejected }} Rejected
          </b-badge>
        </div>
        <b-button
          variant="link"
          class="student-docs-modal-close p-0"
          @click="showStudentDocsModal = false"
        >
          <feather-icon icon="XIcon" size="20" />
        </b-button>
      </div>

      <div v-if="studentDocsLoading" class="text-center py-5">
        <b-spinner variant="primary" />
        <p class="text-muted mt-1 mb-0">Loading documents…</p>
      </div>

      <div v-else class="student-docs-modal-body">
        <!-- Templates (required) -->
        <div class="doc-section mb-2">
          <h6 class="section-heading">
            <feather-icon icon="ClipboardIcon" size="15" class="mr-50" />
            Required Documents
            <b-badge variant="light-primary" class="ml-50">{{
              studentDocs.templates.length
            }}</b-badge>
          </h6>
          <!-- hidden file input for template uploads -->
          <input
            ref="docFileInput"
            type="file"
            hidden
            accept="image/*,.pdf"
            @change="onDocFileSelected"
          />

          <div
            v-if="studentDocs.templates.length === 0"
            class="doc-empty-state"
          >
            <feather-icon icon="FileIcon" size="28" class="text-muted mb-50" />
            <p class="mb-0">No required documents</p>
          </div>
          <div v-else class="doc-grid">
            <div
              v-for="(doc, i) in studentDocs.templates"
              :key="'tpl-' + i"
              class="doc-card"
              :class="docCardClasses(doc)"
            >
              <div
                class="doc-card__thumb"
                @click="onTemplateDocClick(doc, i, 'templates')"
              >
                <b-spinner
                  v-if="
                    uploadingDocIndex === i &&
                    docUploadLoading &&
                    uploadingFrom == 'templates'
                  "
                  variant="primary"
                  small
                />
                <template v-else>
                  <b-img
                    v-if="doc.url && isImage(doc.url)"
                    :src="getDocPreviewUrl(doc.url)"
                    class="doc-thumb-img"
                    alt="preview"
                    @error="onDocPreviewError($event, doc.url)"
                  />
                  <feather-icon
                    v-else-if="doc.url"
                    icon="FileTextIcon"
                    size="32"
                    class="text-primary"
                  />
                  <div v-else class="upload-prompt">
                    <feather-icon
                      icon="UploadCloudIcon"
                      size="28"
                      class="text-primary"
                    />
                    <span class="upload-hint">
                      {{
                        (doc.status || "").toLowerCase() === "rejected"
                          ? "Re-upload"
                          : "Upload"
                      }}
                    </span>
                  </div>
                </template>
              </div>
              <div class="doc-card__info">
                <div
                  class="doc-card__title text-truncate"
                  v-b-tooltip.hover.bottomright
                  :title="doc.meta_title"
                >
                  {{ doc.meta_title || "—" }}
                </div>
                <b-badge
                  :variant="
                    doc._newlyUploaded
                      ? 'light-info'
                      : docStatusVariant(doc.status)
                  "
                  class="doc-status-pill"
                >
                  {{
                    doc._newlyUploaded
                      ? "Ready to Save"
                      : formatDocStatus(doc.status)
                  }}
                </b-badge>
                <div v-if="doc.status == 'rejected'" class="doc-reason">
                  {{ doc.rejectionReason }}
                </div>
                <div v-if="doc.url" class="doc-action-btns mt-25" @click.stop>
                  <b-button
                    size="sm"
                    variant="info"
                    class="doc-action-btn doc-action-btn"
                    @click="openFile(doc.url)"
                  >
                    <feather-icon icon="EyeIcon" size="12" class="mr-25" />
                    View
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Uploaded documents -->
        <div class="doc-section">
          <h6 class="section-heading">
            <feather-icon icon="UploadIcon" size="15" class="mr-50" />
            Uploaded Documents
            <b-badge variant="light-success" class="ml-50">{{
              studentDocs.uploaded.length
            }}</b-badge>
          </h6>
          <div v-if="studentDocs.uploaded.length === 0" class="doc-empty-state">
            <feather-icon
              icon="UploadIcon"
              size="28"
              class="text-muted mb-50"
            />
            <p class="mb-0">No documents uploaded.</p>
          </div>
          <div v-else class="doc-grid">
            <div
              v-for="(doc, i) in studentDocs.uploaded"
              :key="'upl-' + i"
              class="doc-card"
              :class="docCardClasses(doc, true)"
            >
              <div
                class="doc-card__thumb"
                @click="doc.url && openFile(doc.url)"
              >
                <b-spinner
                  v-if="
                    statusChangingId === doc.id ||
                    (uploadingDocIndex === i &&
                      docUploadLoading &&
                      uploadingFrom == 'uploaded')
                  "
                  variant="primary"
                  small
                />
                <template v-else>
                  <b-img
                    v-if="isImage(doc.url)"
                    :src="getDocPreviewUrl(doc.url)"
                    class="doc-thumb-img"
                    alt="preview"
                    @error="onDocPreviewError($event, doc.url)"
                  />
                  <feather-icon
                    v-else
                    icon="FileTextIcon"
                    size="32"
                    class="text-primary"
                  />
                </template>
              </div>

              <div class="doc-card__info">
                <div
                  class="doc-card__title text-truncate"
                  v-b-tooltip.hover.bottomright
                  :title="doc.meta_title"
                >
                  {{ doc.meta_title || "—" }}
                </div>
                <b-badge
                  :variant="
                    doc._newlyUploaded
                      ? 'light-info'
                      : docStatusVariant(doc.status)
                  "
                  class="doc-status-pill"
                >
                  {{
                    doc._newlyUploaded
                      ? "Ready to Save"
                      : formatDocStatus(doc.status)
                  }}
                </b-badge>

                <div
                  v-if="
                    (doc.status || '').toLowerCase() === 'pending' ||
                    (isHeadOffice &&
                      (doc.status || '').toLowerCase() === 'approved')
                  "
                  class="doc-action-btns mt-25"
                  @click.stop
                >
                  <b-button
                    size="sm"
                    variant="primary"
                    class="doc-action-btn doc-action-btn"
                    :disabled="statusChangingId === doc.id || docUploadLoading"
                    @click="triggerDocUpload(i, 'uploaded')"
                  >
                    <feather-icon
                      icon="UploadCloudIcon"
                      size="12"
                      class="mr-25"
                    />
                    Re-upload
                  </b-button>
                </div>

                <div
                  v-if="showDocReviewActions(doc) && !doc._newlyUploaded"
                  class="doc-action-btns"
                  @click.stop
                >
                  <b-button
                    size="sm"
                    variant="success"
                    class="doc-action-btn doc-action-btn--approve"
                    :disabled="statusChangingId === doc.id || docUploadLoading"
                    @click="openDocStatus(doc, 'approved')"
                  >
                    <feather-icon icon="CheckIcon" size="12" class="mr-25" />
                    Approve
                  </b-button>
                  <b-button
                    size="sm"
                    variant="danger"
                    class="doc-action-btn doc-action-btn--reject"
                    :disabled="statusChangingId === doc.id || docUploadLoading"
                    @click="openDocStatus(doc, 'rejected')"
                  >
                    <feather-icon icon="XIcon" size="12" class="mr-25" />
                    Reject
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          class="action-btn-filter"
          @click="showStudentDocsModal = false"
          :disabled="savingDocs"
        >
          Close
        </b-button>
        <b-button
          v-if="hasNewUploads"
          variant="gradient-primary"
          class="action-btn-filter"
          @click="saveDocs"
          :disabled="savingDocs"
        >
          <b-spinner v-if="savingDocs" small class="mr-50" />
          Save Documents
        </b-button>
      </template>
    </b-modal>

    <!-- Document Types Modal -->
    <b-modal
      v-model="showDocTypesModal"
      :title="
        showDocTypeForm
          ? isEditDocType
            ? 'Edit Document Type'
            : 'Add Document Type'
          : 'Manage Document Types'
      "
      size="lg"
      centered
      no-close-on-backdrop
      @hidden="onDocTypesModalHidden"
    >
      <!-- Grid view -->
      <div v-if="!showDocTypeForm">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <b-button
            v-if="rights.add"
            variant="gradient-primary"
            size="sm"
            @click="openDocTypeForm"
          >
            <feather-icon icon="PlusIcon" size="14" class="mr-50" />
            Add
          </b-button>
        </div>

        <div v-if="docTypesLoading" class="text-center py-3">
          <b-spinner variant="primary" />
          <p class="text-muted mt-1 mb-0">Loading document types…</p>
        </div>

        <div v-else>
          <b-table
            class="planner-list-table mt-1"
            :items="docTypes"
            :fields="docTypeFields"
            show-empty
            empty-text="No document types found"
            hover
            responsive
            small
          >
            <template #cell(title)="{ item }">
              <span class="font-weight-bold">{{ item.title }}</span>
            </template>

            <template #cell(status)="{ item }">
              <b-form-checkbox
                v-model="item.isActive"
                switch
                :disabled="statusUpdating"
                @change="updateStatus(item, item.isActive)"
              />
            </template>

            <template #cell(actions)="{ item }">
              <div class="d-flex align-items-center">
                <b-button
                  v-if="rights.edit"
                  variant="gradient-primary"
                  size="sm"
                  v-b-tooltip.hover.left
                  title="Edit"
                  class="action-btn-table"
                  @click="openDocTypeFormEdit(item)"
                >
                  <feather-icon icon="EditIcon" size="14" />
                </b-button>
              </div>
            </template>
          </b-table>
        </div>
      </div>

      <!-- Form view -->
      <div v-else>
        <b-button variant="outline-secondary" size="sm" @click="backToGrid">
          <feather-icon icon="ArrowLeftIcon" size="14" class="mr-50" />
          Back to list
        </b-button>

        <b-form @submit.prevent class="mt-1">
          <div>
            <b-row>
              <b-col md="12">
                <b-form-group label-for="docTypeTitle">
                  <template #label>
                    Title <span class="text-danger">*</span>
                  </template>
                  <b-form-input
                    id="docTypeTitle"
                    v-model.trim="docTypeForm.title"
                    placeholder="Enter document type title"
                  />
                </b-form-group>
              </b-col>

              <b-col md="12">
                <b-form-group label="Classes" label-for="docTypeClasses">
                  <v-select
                    id="docTypeClasses"
                    v-model="selectedClasses"
                    :options="[{ name: 'All Classes', id: 0 }, ...classes]"
                    label="name"
                    :reduce="(c) => c.name"
                    multiple
                    :closeOnSelect="false"
                    placeholder="Select classes (leave blank or choose All Classes)"
                    @input="onClassSelectionChange"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </b-form>
      </div>

      <template #modal-footer>
        <!-- Grid footer -->
        <template v-if="!showDocTypeForm">
          <b-button
            variant="outline-secondary"
            @click="showDocTypesModal = false"
          >
            Close
          </b-button>
        </template>

        <!-- Form footer -->
        <template v-else>
          <b-button variant="outline-secondary" @click="backToGrid">
            Cancel
          </b-button>
          <b-button
            variant="primary"
            @click="saveDocType"
            :disabled="savingDocType"
          >
            <b-spinner v-if="savingDocType" small class="mr-50" />
            {{ isEditDocType ? "Update" : "Save" }}
          </b-button>
        </template>
      </template>
    </b-modal>

    <!-- document status -->
    <b-modal
      v-model="showStatusModal"
      :title="`Confirm ${changedDocLable}`"
      size="sm"
      button-size="sm"
      centered
      :ok-title="changedDocLable"
      :ok-variant="changedDocStatus === 'approved' ? 'success' : 'danger'"
      cancel-title="cancel"
      @ok="changeDocStatus"
      @hidden="onCloseDocStatus"
    >
      <div>{{ changedDocText }}</div>
      <div v-if="changedDocStatus == 'rejected'" class="mt-1">
        <b-form-group label="Rejection Reason" label-class="font-weight-bold">
          <b-form-textarea
            rows="2"
            size="sm"
            v-model.trim="changedDocReason"
            placeholder="Enter rejection reason"
          />
        </b-form-group>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BBadge,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
  BSpinner,
  BAvatar,
  BMedia,
  BImg,
  BFormCheckbox,
  BFormTextarea,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { mapActions } from "vuex";
import {
  getUploadPath,
  uploadToMySkool,
  isImageUrl,
  getMediaPreviewUrl,
  handleDrivePreviewError,
} from "@/utils/gdriveUpload";
import * as XLSX from "xlsx";
import {
  parseStudentDocsPayload,
  parseDocTypesPayload,
  mergeStudentDocs,
  resolveTypeWiseDocuments,
  splitResolvedDocList,
} from "./studentDocsUtils";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BBadge,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BSpinner,
    BAvatar,
    BMedia,
    BImg,
    BFormCheckbox,
    BFormTextarea,
    vSelect,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      reportDomain: this.$store.state.report.replace(/_/g, ""),
      rights: {},
      searchQuery: "",
      statusFilter: "all",
      statusFilterOptions: [
        { label: "All", value: "all" },
        { label: "Pending", value: "pending" },
        { label: "Approved", value: "approved" },
        { label: "Rejected", value: "rejected" },
        { label: "Not uploaded", value: "notUploaded" },
      ],
      loadedTypes: [],
      documents: [],
      dataLoading: false,
      currentPage: 1,
      perPage: 50,
      totalCount: 0,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "30", value: 30 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      fields: [
        { key: "student", label: "Student", sortable: false },
        { key: "status", label: "Status", sortable: false },
        { key: "actions", label: "Actions" },
      ],

      // Student Docs Detail Modal
      showStudentDocsModal: false,
      studentDocsLoading: false,
      selectedStudent: null,
      studentDocs: { templates: [], uploaded: [] },
      uploadingDocIndex: null,
      uploadingFrom: "",
      docUploadLoading: false,
      savingDocs: false,
      statusChangingId: null,
      showStatusModal: false,
      changedDocItem: null,
      changedDocStatus: "",
      changedDocLable: "",
      changedDocText: "",
      changedDocReason: "",

      // Document Types Modal
      showDocTypesModal: false,
      showDocTypeForm: false,
      isEditDocType: false,
      docTypes: [],
      docTypesLoading: false,
      savingDocType: false,
      docTypeFields: [
        { key: "title", label: "Title", sortable: false },
        { key: "status", label: "status" },
        { key: "actions", label: "Actions" },
      ],
      docTypeForm: {
        id: 0,
        title: "",
        type: "student",
        description: "",
        classnames: "",
        campusID: this.$store.state.userData.cId,
        createdBy: 0,
        updatedBy: 0,
        createdOn: null,
        updatedOn: null,
      },
      headOfficeObj: null,
      isHeadOffice: false,
      campuses: [],
      selectedCampusID: this.$store.state.userData.cId,
      // Classes
      classes: [],
      selectedClasses: [],

      exporting: false,
      statusUpdating: false,
      docFilter: 0,
    };
  },
  computed: {
    displayedRange() {
      if (this.totalCount === 0) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    hasNewUploads() {
      return (
        this.studentDocs.templates.some((d) => d._newlyUploaded) ||
        this.studentDocs.uploaded.some((d) => d._newlyUploaded)
      );
    },
    canReviewDocuments() {
      return this.headOfficeObj ? this.isHeadOffice : true;
    },
    modalDocStats() {
      const all = [...this.studentDocs.templates, ...this.studentDocs.uploaded];
      const count = (status) =>
        all.filter(
          (d) => (d.status || "").toLowerCase() === status && !d._newlyUploaded
        ).length;
      return {
        pending: count("pending"),
        approved: count("approved"),
        rejected: count("rejected"),
      };
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
      this.LoadDocuments();
    },
  },
  created() {
    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
    } else {
      this.rights = this.$store.state.rights[this.$route.name];
      this.LoadSettings();
      this.LoadDocuments();
      this.loadDocTypes();
    }
  },
  mounted() {
    this.$nextTick(() => this.updateScrollbarWidth());
    window.addEventListener("resize", this.updateScrollbarWidth);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateScrollbarWidth);
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    getCampus(id) {
      let obj = this.campuses.find((el) => el.id == id);
      return obj ? obj.name || obj.branch : "";
    },

    updateScrollbarWidth() {
      this.$nextTick(() => {
        const tableWrapper = this.$refs.tableWrapper;
        const topScrollbar = this.$refs.topScrollbar;
        const topContent =
          topScrollbar &&
          topScrollbar.querySelector(".table-scrollbar-content");
        if (tableWrapper && topContent) {
          topContent.style.width = `${tableWrapper.scrollWidth}px`;
          topScrollbar.scrollLeft = tableWrapper.scrollLeft;
        }
      });
    },

    syncScroll(event) {
      const tableWrapper = this.$refs.tableWrapper;
      const topScrollbar = this.$refs.topScrollbar;
      if (!tableWrapper || !topScrollbar) return;
      if (event && event.target === tableWrapper) {
        topScrollbar.scrollLeft = tableWrapper.scrollLeft;
      } else if (event && event.target === topScrollbar) {
        tableWrapper.scrollLeft = topScrollbar.scrollLeft;
      }
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.LoadDocuments();
    },

    onStatusFilterChange() {
      this.currentPage = 1;
      this.LoadDocuments();
    },

    onDocFilterChange() {
      this.currentPage = 1;
      this.LoadDocuments();
    },

    onCampusFilterChange() {
      this.currentPage = 1;
      this.LoadDocuments();
    },

    closePageSizeDropdown() {
      if (
        this.$refs.pageSizeSelect &&
        this.$refs.pageSizeSelect.toggleDropdown
      ) {
        this.$refs.pageSizeSelect.toggleDropdown(false);
      }
    },

    onPageChange(page) {
      const nextPage = Number(page) || 1;
      this.currentPage = nextPage;
      this.LoadDocuments(nextPage);
    },

    async exportData() {
      this.exporting = true;

      const keyword = (this.searchQuery || "").trim();
      const pageSize = 100;
      const count =
        this.totalCount < pageSize ? 1 : Math.ceil(this.totalCount / pageSize);

      // Fetch all pages and concatenate results into a single array
      let allRows = [];
      for (let page = 1; page <= count; page++) {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "DocumentDetails/GetStudentDocStatus?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.selectedCampusID +
            "&page=" +
            page +
            "&pageSize=" +
            pageSize +
            "&status=" +
            (this.statusFilter === "all" ? "" : this.statusFilter || "") +
            "&keyword=" +
            encodeURIComponent(keyword) +
            "&doctypeID=" +
            (this.docFilter || 0),
          token: this.$store.state.userData.token,
          returnData: true,
        });
        if (Array.isArray(res?.data)) {
          allRows = allRows.concat(res.data);
        } else if (Array.isArray(res)) {
          allRows = allRows.concat(res);
        }
      }

      const rows = allRows || [];
      if (!rows.length) {
        this.$bvToast.toast("No records to export.", {
          title: this.$t("Excel"),
          variant: "warning",
          solid: true,
        });
        this.exporting = false;
        return;
      }

      try {
        const stamp = new Date().toISOString().slice(0, 10);
        const fname = `student-documents-${stamp}.xlsx`;

        const isTcf = ["myskooltcfdemo", "myskooltcf"].includes(
          this.reportDomain
        );
        const kFed = "Federal Board Registration No";
        const kGr = "GR No";
        const kName = "Student Name";
        const kFather = "Father Name";
        const kClassSec = "Class";
        const kCampus = "Campus";
        const kSch = "Scholarship Status";
        const kStuStat = "Student Status";
        const kTotal = "Total";
        const kPen = "Pending";
        const kApp = "Approved";
        const kRej = "Rejected";
        const kNotUp = "Not Uploaded";

        const asCount = (v) => {
          const n = Number(v);
          return Number.isFinite(n) ? n : 0;
        };

        const expData = rows.map((item) => {
          const det = item.studentDetails || {};
          const ds = item.docsStatus || {};
          const upDocs = item.uploadedDocs || [];
          const row = {};

          if (isTcf) {
            row[kFed] =
              det.federalRegNo != null && det.federalRegNo !== ""
                ? String(det.federalRegNo)
                : "";
          } else {
            row[kGr] =
              item.grNo != null && item.grNo !== "" ? String(item.grNo) : "";
          }

          row[kName] = item.name != null ? String(item.name) : "";
          row[kFather] =
            item.father_name != null ? String(item.father_name) : "";
          row[kClassSec] =
            item.meta_class != null ? String(item.meta_class) : "";
          row[kCampus] = this.getCampus(item.campusID);

          if (isTcf) {
            row[kSch] =
              det.scholarshipStatus != null
                ? String(det.scholarshipStatus)
                : "";
            row[kStuStat] =
              det.studentStatus != null ? String(det.studentStatus) : "";
          }

          row[kTotal] = asCount(ds.total);
          row[kPen] = asCount(ds.pending);
          row[kApp] = asCount(ds.approved);
          row[kRej] = asCount(ds.rejected);
          row[kNotUp] = asCount(ds.notUploaded);

          this.loadedTypes.forEach((el) => {
            row[el.title] =
              upDocs.find((dItem) => dItem.typeID == el.id)?.url || "";
          });

          return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(expData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Documents");
        XLSX.writeFile(workbook, fname);
      } catch (e) {
        console.error("exportData", e);
        this.$bvToast.toast("Could not export Excel.", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.exporting = false;
      }
    },

    async LoadDocuments(requestedPage = this.currentPage) {
      const keyword = (this.searchQuery || "").trim();
      const page = Number(requestedPage) || 1;
      this.currentPage = page;
      this.dataLoading = true;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "DocumentDetails/GetStudentDocStatus?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.selectedCampusID +
            "&page=" +
            page +
            "&pageSize=" +
            this.perPage +
            "&status=" +
            (this.statusFilter === "all" ? "" : this.statusFilter || "") +
            "&keyword=" +
            encodeURIComponent(keyword) +
            "&doctypeID=" +
            (this.docFilter || 0),
          token: this.$store.state.userData.token,
          returnData: true,
        });

        const payload = res && typeof res === "object" ? res : {};
        const container = payload.data !== undefined ? payload.data : payload;

        let records = [];
        if (Array.isArray(container?.data)) {
          records = container.data;
        } else if (Array.isArray(container)) {
          records = container;
        } else if (Array.isArray(payload.data)) {
          records = payload.data;
        }

        const totalFromApi = Number(
          payload?.totalCount ??
            container?.totalCount ??
            payload?.data?.totalCount
        );

        this.documents = records;
        this.loadedTypes = payload?.docTypes || [];

        this.totalCount =
          !isNaN(totalFromApi) && totalFromApi >= 0
            ? totalFromApi
            : records.length;

        const maxPage = Math.max(
          1,
          Math.ceil(this.totalCount / this.perPage) || 1
        );
        if (this.currentPage > maxPage) {
          this.currentPage = maxPage;
          if (maxPage !== page) {
            await this.LoadDocuments(maxPage);
            return;
          }
        }

        this.$nextTick(() => this.updateScrollbarWidth());
      } catch (e) {
        console.error("Load documents error:", e);
        this.documents = [];
        this.totalCount = 0;
        this.$bvToast.toast("Failed to load student documents.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.dataLoading = false;
      }
    },

    // ── Classes ──────────────────────────────────────────────────────────────
    async LoadSettings() {
      let res = await this.post({
        url:
          this.$store.state.domain +
          "Settings/getSettingsAll?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: ["head_office"],
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      });

      this.headOfficeObj = res.find((el) => el.key == "head_office");
      if (this.headOfficeObj) {
        const hoCampusId = this.getHeadOfficeCampusId();
        this.isHeadOffice =
          hoCampusId != null && hoCampusId == this.$store.state.userData.cId;
        this.LoadDistinctClasses();
        this.LoadCampuses();

        this.fields.splice(1, 0, { key: "campus", label: "Campus" });
      } else this.loadClasses();
    },

    getHeadOfficeCampusId() {
      if (!this.headOfficeObj) return null;
      const id =
        this.headOfficeObj.campusID ??
        this.headOfficeObj.valueNumber ??
        this.headOfficeObj.value ??
        this.headOfficeObj.valueInt;
      return id != null && id !== "" ? id : null;
    },
    async LoadCampuses() {
      const res = await this.get({
        url:
          this.$store.state.domain +
          "campuses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      });
      this.campuses = Array.isArray(res) ? res : (res && res.data) || [];
    },
    async LoadDistinctClasses() {
      var obj = {
        url:
          this.$store.state.domain +
          "myclasses/LoadClassesAllDistinct?db=" +
          this.$store.state.userData.db +
          "&dID=0",
        token: this.$store.state.userData.token,
      };
      let res = await this.get(obj);

      this.classes = res.map((el) => el.cls || el);
    },
    async loadClasses() {
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&dID=0",
          token: this.$store.state.userData.token,
        });
        this.classes = res.map((el) => el.cls || el);
        // console.log(this.classes);
      } catch (e) {
        console.error("Failed to load classes", e);
      }
    },

    onClassSelectionChange() {
      if (this.selectedClasses.at(-1) == "All Classes") {
        this.selectedClasses = ["All Classes"];
      } else {
        this.selectedClasses = this.selectedClasses.filter(
          (el) => el != "All Classes"
        );
      }
    },

    // ── Document Types ────────────────────────────────────────────────────────

    async openDocTypesModal() {
      this.showDocTypeForm = false;
      this.isEditDocType = false;
      this.showDocTypesModal = true;
      await this.loadDocTypes();
    },

    async loadDocTypes() {
      this.docTypesLoading = true;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "DocumentTypes/LoadData?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&type=student",
          token: this.$store.state.userData.token,
          returnData: true,
        });
        let records = [];
        if (Array.isArray(res)) {
          records = res;
        } else if (res && Array.isArray(res.data)) {
          records = res.data;
        } else if (res && Array.isArray(res.data && res.data.data)) {
          records = res.data.data;
        }
        this.docTypes = records;
      } catch (e) {
        console.error("Failed to load document types", e);
        this.$bvToast.toast("Failed to load document types.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.docTypesLoading = false;
      }
    },

    openDocTypeForm() {
      this.isEditDocType = false;
      this.resetDocTypeForm();
      this.showDocTypeForm = true;
    },

    openDocTypeFormEdit(item) {
      this.isEditDocType = true;
      this.docTypeForm = { ...item };

      // Populate selectedClasses with names
      if (!item.classnames) {
        this.selectedClasses = ["All Classes"];
      } else {
        this.selectedClasses = item.classnames.split(",");
      }

      this.showDocTypeForm = true;
    },

    backToGrid() {
      this.showDocTypeForm = false;
      this.isEditDocType = false;
      this.resetDocTypeForm();
    },

    resetDocTypeForm() {
      this.docTypeForm = {
        id: 0,
        title: "",
        type: "student",
        description: "",
        classnames: "",
        campusID: this.$store.state.userData.cId,
        isActive: true,
        createdBy: 0,
        updatedBy: 0,
        createdOn: null,
        updatedOn: null,
      };
      this.selectedClasses = [];
    },

    async saveDocType() {
      if (!this.docTypeForm.title || !this.docTypeForm.title.trim()) {
        this.$bvToast.toast("Title is required.", {
          title: "Validation",
          variant: "warning",
          solid: true,
        });
        return;
      }

      // Build classnames
      const hasAll =
        this.selectedClasses.length === 0 ||
        this.selectedClasses.includes("All Classes");
      const classnames = hasAll ? "" : this.selectedClasses.join(",");

      const payload = {
        ...this.docTypeForm,
        classnames,
      };

      this.savingDocType = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "DocumentTypes/Save?db=" +
            this.$store.state.userData.db +
            "&userID=" +
            this.$store.state.userData.userID,
          body: payload,
          message: this.isEditDocType
            ? "Document type updated successfully."
            : "Document type saved successfully.",
          context: this,
          token: this.$store.state.userData.token,
        };
        const ok = await this.post(obj);
        if (ok) {
          this.backToGrid();
          await this.loadDocTypes();
        }
      } catch (e) {
        console.error("Save document type error:", e);
        this.$bvToast.toast("Could not save document type.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.savingDocType = false;
      }
    },

    async updateStatus(item, newStatus) {
      const statusText = newStatus ? "active" : "inactive";
      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: `Do you want to ${statusText} this document type?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes"),
      });

      if (result.isConfirmed) {
        // console.log(item, newStatus);
        this.statusUpdating = true;
        const obj = {
          url:
            this.$store.state.domain +
            "DocumentTypes/Save?db=" +
            this.$store.state.userData.db +
            "&userID=" +
            this.$store.state.userData.userID,
          body: item,
          message: `${item.title} is now ${statusText} successfully!`,
          context: this,
          token: this.$store.state.userData.token,
        };
        const ok = await this.post(obj);

        this.statusUpdating = false;
        if (!ok) {
          // Revert the toggle if API call fails
          item.isActive = !newStatus;
        }
      } else {
        // Revert the toggle if user cancels
        item.isActive = !newStatus;
      }
    },
    // ── Student Docs Detail ───────────────────────────────────────────────────

    async openStudentDocs(item) {
      this.selectedStudent = item;
      this.studentDocs = { templates: [], uploaded: [] };
      this.showStudentDocsModal = true;
      this.studentDocsLoading = true;
      try {
        const campusID =
          item.campusID ||
          (item.studentDetails && item.studentDetails.campusID) ||
          this.$store.state.userData.cId;

        const res = await this.get({
          url:
            this.$store.state.domain +
            "DocumentDetails/LoadStudentDocs?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            campusID +
            "&studentID=" +
            item.id,
          token: this.$store.state.userData.token,
          returnData: true,
        });

        let { templates, uploaded } = parseStudentDocsPayload(res);
        // const docTypes = await this.loadDocTypesForStudent(campusID);
        // const className = item.meta_class || item.class_name || "";
        // const merged = mergeStudentDocs(templates, uploaded);
        // const list = resolveTypeWiseDocuments(
        //   merged.templates,
        //   merged.uploaded,
        //   docTypes,
        //   className
        // );
        // const split = splitResolvedDocList(list, docTypes, className);
        this.studentDocs = {
          templates,
          uploaded,
        };
        // console.log(this.studentDocs);
      } catch (e) {
        console.error("Load student docs error:", e);
        this.$bvToast.toast("Failed to load student documents.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.studentDocsLoading = false;
      }
    },

    async loadDocTypesForStudent(campusID) {
      const res = await this.get({
        url:
          this.$store.state.domain +
          "DocumentTypes/LoadData?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          campusID +
          "&type=student",
        token: this.$store.state.userData.token,
        returnData: true,
      });
      return parseDocTypesPayload(res);
    },

    onStudentDocsModalHidden() {
      this.selectedStudent = null;
      this.studentDocs = { templates: [], uploaded: [] };
      this.uploadingDocIndex = null;
      this.uploadingFrom = "";
      this.docUploadLoading = false;
      this.savingDocs = false;
      this.statusChangingId = null;
    },

    triggerDocUpload(index, uploadFrom) {
      this.uploadingDocIndex = index;
      this.uploadingFrom = uploadFrom;
      this.$nextTick(() => {
        const input = this.$refs.docFileInput;
        if (input) {
          input.value = "";
          input.click();
        }
      });
    },

    async onDocFileSelected() {
      const input = this.$refs.docFileInput;
      if (!input || !input.files || !input.files[0]) return;

      const idx = this.uploadingDocIndex;
      if (idx === null || idx === undefined) return;

      this.docUploadLoading = true;
      try {
        const uploaded = await uploadToMySkool([input.files[0]]);
        const path = getUploadPath(uploaded[0]);
        if (path) {
          // pending re-upload
          if (this.uploadingFrom == "uploaded") {
            this.$set(this.studentDocs.uploaded, idx, {
              ...this.studentDocs.uploaded[idx],
              url: path,
              _newlyUploaded: true,
            });
          } else {
            this.$set(this.studentDocs.templates, idx, {
              ...this.studentDocs.templates[idx],
              url: path,
              _newlyUploaded: true,
            });
          }
        } else {
          this.$bvToast.toast("Could not read upload path from response.", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
        }
      } catch (err) {
        console.log(err);
        this.$bvToast.toast("File upload failed.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.docUploadLoading = false;
        this.uploadingDocIndex = null;
        this.uploadingFrom = "";
        input.value = "";
      }
    },

    async saveDocs() {
      const tempSave = this.studentDocs.templates
        .filter((d) => d._newlyUploaded)
        .map(({ _newlyUploaded, ...rest }) => ({
          ...rest,
          status: this.headOfficeObj
            ? this.isHeadOffice
              ? "approved"
              : "pending"
            : "approved",
          uploadedVia: "campus-admin",
        }));

      const upSave = this.studentDocs.uploaded
        .filter((d) => d._newlyUploaded)
        .map(({ _newlyUploaded, ...rest }) => ({
          ...rest,
          status: this.headOfficeObj
            ? this.isHeadOffice
              ? "approved"
              : "pending"
            : "approved",
          uploadedVia: "campus-admin",
        }));

      const toSave = [...tempSave, ...upSave];
      if (!toSave.length) return;

      this.savingDocs = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "DocumentDetails/SaveDocs?db=" +
            this.$store.state.userData.db +
            "&sID=" +
            this.selectedStudent.id +
            "&userID=" +
            this.$store.state.userData.userID,
          body: toSave,
          message: "Documents saved successfully.",
          context: this,
          token: this.$store.state.userData.token,
        };
        const ok = await this.post(obj);
        if (ok) {
          // Reload to get fresh status
          await this.openStudentDocs(this.selectedStudent);
          this.LoadDocuments();
        }
      } catch (e) {
        console.error("Save docs error:", e);
        this.$bvToast.toast("Could not save documents.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.savingDocs = false;
      }
    },

    isImage(url) {
      return isImageUrl(url);
    },

    getDocPreviewUrl(url) {
      return getMediaPreviewUrl(url, "Image");
    },

    onDocPreviewError(event, url) {
      handleDrivePreviewError(event, url, "image");
    },

    openFile(url) {
      if (url) window.open(url, "_blank");
    },

    showDocReviewActions(doc) {
      return (
        this.canReviewDocuments &&
        doc &&
        doc.id &&
        (doc.status || "").toLowerCase() === "pending"
      );
    },

    formatDocStatus(status) {
      const s = (status || "").trim();
      if (!s) return "Not Uploaded";
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    },

    docCardClasses(doc, uploadedOnly = false) {
      const status = (doc.status || "").toLowerCase();
      const canUpload = !uploadedOnly && (!status || status === "rejected");
      const canOpen = !!doc.url && status !== "rejected";
      return {
        "doc-card--clickable": canUpload || canOpen,
        "doc-card--upload": canUpload,
        "doc-card--pending": status === "pending",
        "doc-card--approved": status === "approved",
        "doc-card--rejected": status === "rejected",
      };
    },

    onTemplateDocClick(doc, index, uploadFrom) {
      const status = (doc.status || "").toLowerCase();
      if (!status || status === "rejected") {
        this.triggerDocUpload(index, uploadFrom);
      } else if (doc.url) {
        this.openFile(doc.url);
      }
    },

    docStatusVariant(status) {
      const s = (status || "").toLowerCase();
      if (s === "approved") return "light-success";
      if (s === "pending") return "light-warning";
      if (s === "rejected") return "light-danger";
      return "light-secondary";
    },

    openDocStatus(doc, status) {
      this.changedDocItem = doc;
      this.changedDocStatus = status;
      this.changedDocLable = status === "approved" ? "Approve" : "Reject";
      this.changedDocText = `${this.changedDocLable} the document "${
        doc.meta_title || "this document"
      }"?`;
      this.changedDocReason = "";
      this.showStatusModal = true;
    },
    onCloseDocStatus() {
      this.changedDocItem = null;
      this.changedDocStatus = "";
      this.changedDocLable = "";
      this.changedDocText = "";
      this.changedDocReason = "";
    },
    async changeDocStatus(bvModalEvt) {
      if (bvModalEvt) bvModalEvt.preventDefault();
      this.statusChangingId = this.changedDocItem.id;

      try {
        const obj = {
          url:
            this.$store.state.domain +
            "DocumentDetails/ChangeStatus?db=" +
            this.$store.state.userData.db +
            "&docID=" +
            this.changedDocItem.id +
            "&status=" +
            this.changedDocStatus +
            "&userID=" +
            this.$store.state.userData.userID +
            "&sID=" +
            this.selectedStudent.id +
            "&reason=" +
            (this.changedDocReason || ""),
          body: {},
          message: `Document ${this.changedDocStatus} successfully.`,
          context: this,
          token: this.$store.state.userData.token,
        };
        this.showStatusModal = false;

        const ok = await this.post(obj);
        if (ok) {
          await this.openStudentDocs(this.selectedStudent);
          this.LoadDocuments();
        }
      } catch (e) {
        console.error("ChangeStatus error:", e);
        this.$bvToast.toast("Could not update document status.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.statusChangingId = null;
      }
    },

    onDocTypesModalHidden() {
      this.showDocTypeForm = false;
      this.isEditDocType = false;
      this.resetDocTypeForm();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../planner/plannerListTable.scss";

.student-docs-list-card {
  overflow: visible;
}

.student-docs-table {
  ::v-deep td {
    white-space: normal;
  }
}

.table-scroll-container {
  z-index: unset !important;
}

.table-scrollbar-top {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  height: 12px;
  margin-bottom: 8px;

  .table-scrollbar-content {
    height: 1px;
  }
}

.table-wrapper {
  overflow-x: auto;
  width: 100%;
}

.docs-status-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.status-pill {
  font-size: 0.72rem;
  display: inline-flex;
  align-items: center;
}

/* ── Student Docs Modal ── */
.student-docs-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.25rem 0 1rem;
  margin-bottom: 0.5rem;
  border-bottom: 2px solid #f0edff;
  flex-wrap: wrap;
}

.student-docs-modal-header__info {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
}

.student-docs-modal-title {
  font-size: 1.15rem;
  font-weight: 800;
  color: #2d1b69;
}

.student-docs-modal-header__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: center;
}

.student-docs-modal-close {
  color: #6b7280 !important;
  flex-shrink: 0;

  &:hover {
    color: #2d1b69 !important;
  }
}

.student-docs-modal-body {
  padding-top: 0.25rem;
}

.doc-section {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e8e5fb;
  border-radius: 16px;
  padding: 1rem 1.15rem;
}

.section-heading {
  font-size: 0.9rem;
  font-weight: 700;
  color: #2d1b69;
  display: flex;
  align-items: center;
  margin-bottom: 0.85rem;
}

.doc-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  color: #6b7280;
  font-size: 0.875rem;
  background: #f8f7ff;
  border: 1.5px dashed #d0c9f0;
  border-radius: 12px;
}

.doc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 14px;
}

.doc-card {
  border: 1.5px solid #e0d8ff;
  border-radius: 14px;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.06);
}

.doc-card--clickable {
  cursor: pointer;

  &:hover {
    border-color: #7c5dfa;
    box-shadow: 0 8px 20px rgba(124, 93, 250, 0.18);
    transform: translateY(-2px);
  }
}

.doc-card--upload {
  border-style: dashed;
  border-color: #b8aef7;
  background: #f8f7ff;

  &:hover {
    background: #f3f0ff;
  }
}

.doc-card--pending {
  border-color: #ffcf5c;
  background: linear-gradient(180deg, #fffdf5 0%, #ffffff 100%);
}

.doc-card--approved {
  border-color: #28c76f;
}

.doc-card--rejected {
  border-color: #ea5455;
  background: linear-gradient(180deg, #fff8f8 0%, #ffffff 100%);
}

.upload-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.upload-hint {
  font-size: 0.68rem;
  color: #7c5dfa;
  font-weight: 600;
}

.doc-card__thumb {
  width: 100%;
  height: 88px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #f3f0ff 0%, #faf9ff 100%);
  overflow: hidden;
}

.doc-thumb-img {
  width: 100%;
  height: 88px;
  object-fit: cover;
}

.doc-card__info {
  width: 100%;
  padding: 8px 8px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.doc-card__title {
  font-size: 0.75rem;
  font-weight: 700;
  color: #2d1b69;
  text-align: center;
  max-width: 100%;
}

.doc-reason {
  font-size: 0.75rem;
  font-weight: bold;
  text-align: center;
}

.doc-status-pill {
  font-size: 0.65rem;
  font-weight: 600;
}

.doc-action-btns {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.doc-action-btn {
  width: 100%;
  padding: 3px 8px;
  border-radius: 8px;
  line-height: 1.2;
  font-size: 0.68rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .student-docs-modal-header__stats {
    width: 100%;
  }

  .doc-grid {
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.student-docs-modal-content {
  border-radius: 20px !important;
  border: 1px solid #e0d8ff !important;
  overflow: hidden;
}

.student-docs-modal .modal-body {
  padding: 1.25rem 1.5rem;
}

.student-docs-modal .modal-footer {
  border-top: 2px solid #f0edff;
  padding: 0.85rem 1.5rem;
}
</style>
